import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
} from '@tanstack/react-table';
import {
  CaretUp,
  CaretDown,
  Clock,
  Phone,
  Spinner,
  X,
} from '@phosphor-icons/react';

import Page from '@/components/page/Page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/common/data-table/data-table';
import { ForegroundAgentChat } from '@/components/common/ForegroundAgentChat';
import { useARVapiCall } from '@/hooks/useARVapiCall';

import { MOCK_CUSTOMERS, type ARCustomer } from '../data/mockARData';

function formatCurrency(amount: number, compact = false): string {
  if (compact || amount >= 1_000_000) {
    if (amount >= 1_000_000) {
      return `$${(amount / 1_000_000).toFixed(1)}M`;
    }
    if (amount >= 1_000) {
      return `$${(amount / 1_000).toFixed(0)}K`;
    }
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatScheduledDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 0) return 'Overdue';
  if (diffHours < 24) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

type StatusFilter = 'all' | 'needs_attention' | 'in_progress' | 'on_track';

const STATUS_CONFIG = {
  needs_attention: {
    label: 'Needs Attention',
    variant: 'warning' as const,
  },
  in_progress: {
    label: 'In Progress',
    variant: 'secondary' as const,
  },
  on_track: {
    label: 'On Track',
    variant: 'outline' as const,
  },
};

export function ARCollectionsPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'pastDueAmount', desc: true },
  ]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, ARCustomer['status']>
  >({});
  const [isResolving, setIsResolving] = useState(false);
  const [showCallsPanel, setShowCallsPanel] = useState(false);

  // Vapi call hook for AR collections
  const {
    customerCalls,
    startBatchCalls,
    endCall,
    endAllCalls,
    isAnyCallActive,
    clearCalls,
  } = useARVapiCall();

  // Apply status overrides to customers
  const customersWithOverrides = useMemo(
    () =>
      MOCK_CUSTOMERS.map((c) => ({
        ...c,
        status: statusOverrides[c.id] ?? c.status,
      })),
    [statusOverrides],
  );

  // Filter customers based on status
  const filteredCustomers = useMemo(
    () =>
      statusFilter === 'all'
        ? customersWithOverrides
        : customersWithOverrides.filter((c) => c.status === statusFilter),
    [statusFilter, customersWithOverrides],
  );

  // Count needs attention customers
  const needsAttentionCustomers = useMemo(
    () => customersWithOverrides.filter((c) => c.status === 'needs_attention'),
    [customersWithOverrides],
  );

  const handleResolveAllIssues = useCallback(async () => {
    setIsResolving(true);

    // Set all needs_attention to in_progress
    const overrides: Record<string, ARCustomer['status']> = {};
    for (const customer of needsAttentionCustomers) {
      overrides[customer.id] = 'in_progress';
    }

    setStatusOverrides((prev) => ({ ...prev, ...overrides }));

    // Start Vapi calls for the first 2 customers that need attention
    // The backend maps customer IDs to demo phone numbers:
    // - target → Angel (646-657-5539)
    // - costco → Noam (857-310-7458)
    const customersToCall = needsAttentionCustomers.slice(0, 2).map((c) => ({
      id: c.id,
      name: c.name,
      contactPerson: c.contactPerson,
      overdueAmount: formatCurrency(c.pastDueAmount),
      invoiceCount: c.openInvoicesCount,
      oldestInvoiceDays: c.aging?.over90 ? 90 : c.aging?.days60 ? 60 : 30,
    }));

    if (customersToCall.length > 0) {
      setShowCallsPanel(true);
      await startBatchCalls(customersToCall);
    }

    setIsResolving(false);
    console.log(
      'Resolved issues for customers:',
      needsAttentionCustomers.map((c) => c.id),
    );
  }, [needsAttentionCustomers, startBatchCalls]);

  const handleRowClick = (customer: ARCustomer) => {
    navigate({
      to: '/ar/collections/$customerId',
      params: { customerId: customer.id },
    });
  };

  const columns: ColumnDef<ARCustomer>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Customer',
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.name}</div>
            <div className="text-xs text-muted-foreground">
              {row.original.accountNumber}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'pastDueAmount',
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-foreground"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Overdue Balance
            {column.getIsSorted() === 'asc' ? (
              <CaretUp className="h-3 w-3" />
            ) : column.getIsSorted() === 'desc' ? (
              <CaretDown className="h-3 w-3" />
            ) : null}
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="font-medium">
            {formatCurrency(getValue<number>())}
          </span>
        ),
      },
      {
        accessorKey: 'openInvoicesCount',
        header: 'Open Invoices',
        cell: ({ getValue }) => (
          <span className="text-muted-foreground">{getValue<number>()}</span>
        ),
      },
      {
        accessorKey: 'lastContactDate',
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-foreground"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Last Contact
            {column.getIsSorted() === 'asc' ? (
              <CaretUp className="h-3 w-3" />
            ) : column.getIsSorted() === 'desc' ? (
              <CaretDown className="h-3 w-3" />
            ) : null}
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="text-muted-foreground">
            {formatRelativeTime(getValue<string>())}
          </span>
        ),
      },
      {
        accessorKey: 'nextScheduledOutreach',
        header: 'Next Outreach',
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          if (!value) {
            return <span className="text-muted-foreground">—</span>;
          }
          return (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span>{formatScheduledDate(value)}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const status = getValue<ARCustomer['status']>();
          const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.on_track;
          return <Badge variant={config.variant}>{config.label}</Badge>;
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredCustomers,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Calculate summary stats
  const totalOverdue = customersWithOverrides.reduce(
    (sum, c) => sum + c.pastDueAmount,
    0,
  );
  const needsAttentionCount = needsAttentionCustomers.length;

  return (
    <Page title="Collections">
      <div className="space-y-4">
        {/* Summary Stats */}
        <div className="flex gap-4">
          <Card className="flex-1">
            <CardContent className="pt-4 pb-4">
              <div className="text-sm text-muted-foreground">Total Overdue</div>
              <div className="text-2xl font-bold">
                {formatCurrency(totalOverdue)}
              </div>
            </CardContent>
          </Card>
          <Card className="flex-1">
            <CardContent className="pt-4 pb-4">
              <div className="text-sm text-muted-foreground">
                Customers with Overdue
              </div>
              <div className="text-2xl font-bold">
                {customersWithOverrides.length}
              </div>
            </CardContent>
          </Card>
          <Card className="flex-1">
            <CardContent className="pt-4 pb-4">
              <div className="text-sm text-muted-foreground">
                Needs Attention
              </div>
              <div className="text-2xl font-bold text-red-600">
                {needsAttentionCount}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2">
          {(
            [
              { key: 'all', label: 'All' },
              { key: 'needs_attention', label: 'Needs Attention' },
              { key: 'in_progress', label: 'In Progress' },
              { key: 'on_track', label: 'On Track' },
            ] as const
          ).map(({ key, label }) => (
            <Button
              key={key}
              variant={statusFilter === key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(key)}
            >
              {label}
              {key !== 'all' && (
                <span className="ml-1.5 text-xs opacity-70">
                  {
                    customersWithOverrides.filter((c) => c.status === key)
                      .length
                  }
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Customer Table */}
        <Card>
          <DataTable table={table} onRowClick={handleRowClick} />
        </Card>
      </div>

      {/* Active Calls Panel */}
      {showCallsPanel && customerCalls.size > 0 && (
        <div className="fixed bottom-4 right-4 z-40 w-96 max-h-[60vh] overflow-auto">
          <Card className="shadow-xl border-2">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Collection Calls
                {isAnyCallActive && (
                  <Spinner className="h-4 w-4 animate-spin text-primary" />
                )}
              </CardTitle>
              <div className="flex gap-1">
                {isAnyCallActive && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={endAllCalls}
                    className="h-6 px-2 text-xs text-red-600 hover:text-red-700"
                  >
                    End All
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => {
                    if (!isAnyCallActive) {
                      clearCalls();
                    }
                    setShowCallsPanel(false);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              {Array.from(customerCalls.values()).map((call) => (
                <div
                  key={call.customerId}
                  className="border rounded-lg p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">
                      {call.customerName}
                    </span>
                    <Badge
                      variant={
                        call.status === 'active'
                          ? 'default'
                          : call.status === 'ended'
                            ? 'outline'
                            : call.status === 'error'
                              ? 'destructive'
                              : 'secondary'
                      }
                    >
                      {call.status === 'connecting'
                        ? 'Connecting...'
                        : call.status === 'ringing'
                          ? 'Ringing...'
                          : call.status === 'active'
                            ? 'On Call'
                            : call.status === 'ended'
                              ? 'Ended'
                              : call.status === 'error'
                                ? 'Error'
                                : call.status}
                    </Badge>
                  </div>
                  {call.error && (
                    <div className="text-xs text-red-600">{call.error}</div>
                  )}
                  {call.transcript.length > 0 && (
                    <div className="max-h-32 overflow-y-auto space-y-1 text-xs bg-muted/50 rounded p-2">
                      {call.transcript.map((msg) => (
                        <div key={msg.id} className="flex gap-2">
                          <span
                            className={
                              msg.role === 'assistant'
                                ? 'text-primary font-medium'
                                : 'text-muted-foreground'
                            }
                          >
                            {msg.role === 'assistant' ? 'AI:' : 'Customer:'}
                          </span>
                          <span className="flex-1">{msg.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(call.status === 'active' ||
                    call.status === 'connecting' ||
                    call.status === 'ringing') && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => endCall(call.customerId)}
                    >
                      End Call
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      <ForegroundAgentChat
        moduleKey="ar"
        currentPage="/ar/collections"
        onResolveARIssues={async (payload) => {
          // When AI triggers resolve, set all needs_attention to in_progress
          if (payload.action === 'resolve_all') {
            await handleResolveAllIssues();
          }
        }}
      />
    </Page>
  );
}
